import { IMeterpoint } from "@/models/meterpoint/meterpoint.types"
import { IRegister } from "@/models/meterRegister/meterRegister.types"
import {
  MeterReading,
  MeterReadingType,
} from "@/api/backofficeRest/generated/model"
import { IEstimatorResultRow } from "./estimatorResultRows"

/**
 * Both granted to the metering, billing and payments roles only (SO-32409). The gateway
 * checks the same roles on submit, so hiding the controls is the courtesy and
 * the 403 is the gate.
 */
export const ESTIMATOR_PERMISSIONS = {
  toggleSeasonality: "agent.meterReadEstimator.toggleSeasonality",
  submitSystemRead: "agent.meterReadEstimator.submitSystemRead",
} as const

export const SEASONALITY_GUIDANCE =
  "Non-seasonal estimates should only be used to check past calculations; seasonality applied is the default"

/**
 * Mirrors the gateway's cap on the Junifer reading description. The gateway
 * builds the text itself from the caller's token, so this only shapes the
 * preview the agent confirms - it never decides what Junifer stores.
 */
export const DESCRIPTION_MAX_LENGTH = 1000

export const estimatedReadDescription = (
  email: string,
  calculationDetail: string,
): string => {
  const header = `Added by ${email} via the Meter Read Estimator in Nova`
  const detail = calculationDetail.trim()
  if (!detail) return header.slice(0, DESCRIPTION_MAX_LENGTH)

  const full = `${header}\n\n${detail}`
  return full.length <= DESCRIPTION_MAX_LENGTH
    ? full
    : `${full.slice(0, DESCRIPTION_MAX_LENGTH - 1)}…`
}

export interface ICalculationDetailContext {
  modeTitle: string
  /** YYYY-MM-DD, the same form as the dates in the inputs line. */
  targetDate: string
  seasonal: boolean
  volumeUnitLabel: string
  calorificValueApplied: number | null
}

/** Enough to see the rounding the read applied, without float noise. */
export const formatRawProjection = (value: number): string => value.toFixed(2)

const weight = (value: number | null): string =>
  value === null ? "-" : value.toFixed(3)

const sentenceCase = (label: string): string =>
  label.charAt(0).toUpperCase() + label.slice(1)

/**
 * The result's Breakdown panel as plain text, so whoever opens the read in
 * Junifer can see how it was reached without access to Nova. Same lines, same
 * wording, one block per register.
 */
export const buildCalculationDetail = (
  rows: IEstimatorResultRow[],
  context: ICalculationDetailContext,
): string => {
  const shared = [
    `Mode: ${context.modeTitle}`,
    `Seasonality: ${context.seasonal ? "on" : "off"}`,
  ]
  if (context.volumeUnitLabel) {
    shared.push(`Volume unit: ${context.volumeUnitLabel}`)
  }
  if (context.calorificValueApplied !== null) {
    shared.push(`Calorific value applied: ${context.calorificValueApplied}`)
  }

  const perRegister = rows.map((row) => {
    const lines = [
      `Estimated read: ${row.read} on ${context.targetDate}`,
      `Inputs: ${row.inputsSummary}`,
    ]
    if (row.knownPeriodDays !== null) {
      lines.push(`Between the reads: ${row.knownPeriodDays} days`)
    }
    if (row.knownPeriodWeight !== null) {
      lines.push(
        `Weight for that period (ALP): ${weight(row.knownPeriodWeight)}`,
      )
    }
    if (row.projectionDays !== null) {
      lines.push(
        `${sentenceCase(row.laterReadLabel)} to target date: ${row.projectionDays} days`,
      )
    }
    lines.push(
      `Weight for that period: ${weight(row.projectionWeight)}`,
      `Raw projection: ${formatRawProjection(row.estimate.estimatedRead)}`,
    )

    return rows.length > 1
      ? [`${row.registerLabel} register`, ...lines].join("\n")
      : lines.join("\n")
  })

  return [shared.join("\n"), ...perRegister].join("\n\n")
}

/**
 * Only an estimate the agent could have copied off the screen is offered for
 * submission: a negative projection has no meter reading to write, and a
 * mismatch between rows and registers means the response no longer lines up
 * with the form.
 */
export const canSubmitRows = (
  rows: IEstimatorResultRow[],
  registers: IRegister[],
): boolean =>
  rows.length > 0 &&
  rows.length === registers.length &&
  rows.every((row) => !row.wentNegative)

/**
 * One reading per register, all on the target date, in the shape the existing
 * submit path already uses. Registers are matched to the response by position,
 * as the result rows are. Null when a register cannot be traced to its meter,
 * since the gateway needs the meter identifier for every reading.
 */
export const toEstimatedReadingInputs = (
  rows: IEstimatorResultRow[],
  registers: IRegister[],
  meterpoint: IMeterpoint,
  targetDate: string,
): MeterReading[] | null => {
  const readings: MeterReading[] = []

  for (const [index, row] of rows.entries()) {
    const register = registers[index]
    const meter = meterpoint.meters.find((candidate) =>
      candidate.registers.some((owned) => owned.id === register?.id),
    )
    if (!register || !meter) return null

    readings.push({
      date: targetDate,
      reading: Math.round(row.estimate.estimatedReadFormatted),
      type: MeterReadingType.Normal,
      meter: meter.identifier,
      meterPoint: meterpoint.id,
      register: register.identifier,
    })
  }

  return readings
}
